import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FormControl, FormControlLabel, Grid, MenuItem, Select, SelectChangeEvent, Switch, TableContainer, Typography } from '@mui/material'
import { Box } from '@mui/system'

import { AnalystOverviewDialog } from '../../../../components/AnalystOverviewDialog/AnalystOverviewDialog'
import { GRTable, GRTableColumn, SortOrder } from '../../../../components/GRTable/GRTable'
import { GRTooltip } from '../../../../components/GRTooltip/GRTooltip'
import { ReviewIndicator } from '../../../../components/ReviewIndicator/ReviewIndicator'
import { TrendIndicator, TrendIndicatorType } from '../../../../components/TrendIndicator/TrendIndicator'
import utilsService from '../../../../services/UtilsService'
import { useMarketShareAceessCheck } from '../../../account/hooks/roleHooks'
import QuarterSelector, { QuarterSelectorType } from '../../../quarter/components/QuarterSelector'
import quarterService, { QuarterDataKey } from '../../../quarter/services/QuarterService'
import { Quarter } from '../../../quarter/types/Quarter'
import { TopGameCardContent } from '../../../top-game/components/TopGameCard/TopGameCard'
import { TopGame } from '../../../top-game/types/TopGame'
import { buildTopGamesTop500Filter } from '../../helpers/topGamesFilter'
import { MarketShareNumberType, MarketShareNumberTypeId } from '../../types/MarketShareNumberType'
import { MarketShareScopeType } from '../../types/MarketShareScopeType'
import { MarketShareScopeTypeFilter, MarketShareScopeTypeFilterAll } from '../../types/MarketShareScopeTypeFilter'
import { MarketShareSort, MarketShareSortColumnId, defaultMarketShareSort } from '../../types/MarketShareSortColumn'

interface Props {
  calculatedQuarters: Quarter[]
  topGamesWithGameQuarters: TopGame[]
  marketShareScopeType: MarketShareScopeType
  marketShareNumberType: MarketShareNumberType
  selectedQuarters: Quarter[]
  marketShareScopeTypeFilter?: MarketShareScopeTypeFilter
  marketShareScopeTypeFilters: MarketShareScopeTypeFilter[]
  onMarketShareScopeTypeFilterChangeCallback?: (filter: MarketShareScopeTypeFilter) => void
  onQuartersChangeCallback?: (quarterIdentifier1: string, quarterIdentifier2: string) => void
  maxRows?: number
  selectedSubgenres?: string[]
  quarterType?: QuarterSelectorType
  filterGenreType?: 'string' | 'select'
  sort?: MarketShareSort
  onSortChange?: (sort: MarketShareSort) => void
}

const MarketShareTopGames: React.FC<Props> = ({
  calculatedQuarters,
  topGamesWithGameQuarters,
  marketShareScopeType,
  marketShareNumberType,
  selectedQuarters,
  marketShareScopeTypeFilter,
  marketShareScopeTypeFilters,
  onMarketShareScopeTypeFilterChangeCallback,
  onQuartersChangeCallback,
  maxRows,
  selectedSubgenres,
  quarterType = QuarterSelectorType.select,
  filterGenreType,
  sort,
  onSortChange,
}) => {
  const { t } = useTranslation()
  const [showScopeTypeShare, setShowScopeTypeShare] = useState(false)
  const [analystOverviewDialogGame, setAnalystOverviewDialogGame] = useState<TopGame | null>(null)
  const hasAccessToMarketShare = useMarketShareAceessCheck()

  // Sort columns supported by the Top 500 Games table. The shared sort state from MarketSharePage
  // may reference a column that only exists in MarketShareQuarterComparison (e.g. `Name`). In that
  // case we fall back to the default sort here so the table is still sorted in a sensible way,
  // while leaving the parent sort state untouched so it is restored when switching tabs.
  const supportedSortColumnIds = useMemo<readonly MarketShareSortColumnId[]>(
    () => [
      MarketShareSortColumnId.Q1Revenue,
      MarketShareSortColumnId.Q1RevenueChange,
      MarketShareSortColumnId.Q1Downloads,
      MarketShareSortColumnId.Q1DownloadsChange,
      MarketShareSortColumnId.Q2Revenue,
      MarketShareSortColumnId.Q2RevenueChange,
      MarketShareSortColumnId.Q2Downloads,
      MarketShareSortColumnId.Q2DownloadsChange,
    ],
    []
  )
  const activeSort: MarketShareSort = sort && supportedSortColumnIds.includes(sort.columnId) ? sort : defaultMarketShareSort

  // A scope filter (subgenre / genre / publisher) is active when the selected filter is not "All".
  const isScopeFiltered = !!marketShareScopeTypeFilter && marketShareScopeTypeFilter.id !== MarketShareScopeTypeFilterAll

  // The active sort column + whether a scope filter is active determine which Top 500 games show.
  // Membership uses each game's recomputed `downloadsTop500Value` / `revenueTop500Value` (the same
  // values that drive the displayed %), so a listed game always has a non-zero % and 0% rows never
  // appear:
  //  - Whole market (no scope filter) + a VALUE sort (Q1/Q2 Revenue or Downloads): restrict to the
  //    SORTED quarter's Top 500 for that metric, so the list contains no out-of-Top-500 (0%)
  //    entries. (GR-2878, GR-2860)
  //  - Scope-filtered views + Change sorts / unsupported: union across selected quarters by the
  //    active sort metric, so the set stays stable across both quarter columns and Change columns
  //    still surface games that appeared / disappeared between quarters. (GR-2511, GR-2476)
  // See `buildTopGamesTop500Filter` for the full rules + unit tests.
  const filterGamesBySelectedQuartersTop500 = useMemo(
    () => buildTopGamesTop500Filter({ selectedQuarters, sortColumnId: activeSort.columnId, isScopeFiltered }),
    [selectedQuarters, activeSort.columnId, isScopeFiltered]
  )

  const filteredTopGames = useMemo(() => {
    if (selectedSubgenres && selectedSubgenres.length) {
      return topGamesWithGameQuarters.filter((game) => selectedSubgenres.includes(game.conventionalSubgenreId))
    }

    const filteredTopGameWithMarketShare =
      marketShareScopeTypeFilter?.id === MarketShareScopeTypeFilterAll
        ? topGamesWithGameQuarters
        : topGamesWithGameQuarters.filter((game) => {
            return game[marketShareScopeType.gameDataValueId] === marketShareScopeTypeFilter?.id
          })

    return filteredTopGameWithMarketShare.filter(filterGamesBySelectedQuartersTop500)
  }, [selectedSubgenres, marketShareScopeTypeFilter?.id, topGamesWithGameQuarters, filterGamesBySelectedQuartersTop500, marketShareScopeType.gameDataValueId])

  const getGameMarketSharePercent = useCallback(
    (game: TopGame, valueKey: QuarterDataKey.RevenuePercentage | QuarterDataKey.DownloadsPercentage, selectedQuarter: Quarter) => {
      const value =
        showScopeTypeShare && marketShareScopeTypeFilter && marketShareScopeTypeFilter.id !== MarketShareScopeTypeFilterAll
          ? game.selectedScopeTypeQuarters[selectedQuarter.quarterIdentifier][valueKey]
          : game.marketLevelQuarters[selectedQuarter.quarterIdentifier][valueKey]
      return utilsService.formatPercent(value, {
        maximumFractionDigits: 2,
      })
    },
    [showScopeTypeShare, marketShareScopeTypeFilter]
  )

  const handleQuarterChange = useCallback(
    (event: SelectChangeEvent, index: 0 | 1) => {
      const value = event.target.value as string
      let quarterIdentifierArray: string[] = quarterService.mirrorSelectedQuartersIfNeeded(value, index, selectedQuarters)
      if (onQuartersChangeCallback) {
        onQuartersChangeCallback(quarterIdentifierArray[0], quarterIdentifierArray[1])
      }
    },
    [selectedQuarters, onQuartersChangeCallback]
  )

  const handleMarketShareScopeTypeFilterChange = useCallback(
    (event: SelectChangeEvent, marketShareScopeTypeFilters: MarketShareScopeTypeFilter[]) => {
      const value = event.target.value as string
      const newFilter = marketShareScopeTypeFilters.find((filter) => {
        return filter.id === value
      })

      if (newFilter && onMarketShareScopeTypeFilterChangeCallback) {
        onMarketShareScopeTypeFilterChangeCallback(newFilter)
      }
    },
    [onMarketShareScopeTypeFilterChangeCallback]
  )

  const getGameMarketShareValue = useCallback(
    (
      game: TopGame,
      valueKey: QuarterDataKey.RevenueTop500Value | QuarterDataKey.DownloadsTop500Value | QuarterDataKey.RevenueValue | QuarterDataKey.DownloadsValue,
      selectedQuarter: Quarter,
      isCurrency: boolean = false
    ) => {
      const value = game.marketLevelQuarters[selectedQuarter.quarterIdentifier][valueKey]
      if (isCurrency) {
        return utilsService.formatCurrency(value, {
          shorten: true,
          mantissa: 2,
        })
      } else {
        return utilsService.formatNumber(value, {
          shorten: true,
          mantissa: 2,
        })
      }
    },
    []
  )

  const getGameMarketShareForKey = useCallback(
    (game: TopGame, valueKey: 'revenue' | 'downloads', selectedQuarter: Quarter) => {
      switch (valueKey) {
        case 'revenue':
          return marketShareNumberType.id === MarketShareNumberTypeId.Percentage
            ? getGameMarketSharePercent(game, QuarterDataKey.RevenuePercentage, selectedQuarter)
            : getGameMarketShareValue(game, QuarterDataKey.RevenueValue, selectedQuarter, true)

        case 'downloads':
          return marketShareNumberType.id === MarketShareNumberTypeId.Percentage
            ? getGameMarketSharePercent(game, QuarterDataKey.DownloadsPercentage, selectedQuarter)
            : getGameMarketShareValue(game, QuarterDataKey.DownloadsValue, selectedQuarter, false)
      }
    },
    [getGameMarketSharePercent, getGameMarketShareValue, marketShareNumberType.id]
  )

  const getGameMarketShareChange = useCallback(
    (game: TopGame, valueKey: 'revenue' | 'downloads', quarterIdentifier1: string, quarterIdentifier2: string) => {
      const quarters =
        showScopeTypeShare && marketShareScopeTypeFilter && marketShareScopeTypeFilter.id !== MarketShareScopeTypeFilterAll
          ? game.selectedScopeTypeQuarters
          : game.marketLevelQuarters
      switch (valueKey) {
        case 'revenue':
          return marketShareNumberType.id === MarketShareNumberTypeId.Percentage
            ? quarterService.getChangeDataForComparisonQuarterValue(QuarterDataKey.RevenuePercentage, quarterIdentifier1, quarterIdentifier2, quarters) * 100
            : quarterService.getChangeDataForComparisonQuarterValue(QuarterDataKey.RevenueValue, quarterIdentifier1, quarterIdentifier2, quarters)

        case 'downloads':
          return marketShareNumberType.id === MarketShareNumberTypeId.Percentage
            ? quarterService.getChangeDataForComparisonQuarterValue(QuarterDataKey.DownloadsPercentage, quarterIdentifier1, quarterIdentifier2, quarters) * 100
            : quarterService.getChangeDataForComparisonQuarterValue(QuarterDataKey.DownloadsValue, quarterIdentifier1, quarterIdentifier2, quarters)
      }
    },
    [showScopeTypeShare, marketShareNumberType.id, marketShareScopeTypeFilter]
  )

  const customProps = useMemo(() => {
    return {
      marketShareScopeType,
      marketShareNumberType,
      selectedQuarters,
      showScopeTypeShare,
      setShowScopeTypeShare,
      marketShareScopeTypeFilters,
      marketShareScopeTypeFilter,
      onMarketShareScopeTypeFilterChangeCallback,
      getGameMarketShareForKey,
      getGameMarketShareChange,
      handleQuarterChange,
    }
  }, [
    marketShareScopeType,
    marketShareNumberType,
    selectedQuarters,
    showScopeTypeShare,
    marketShareScopeTypeFilters,
    marketShareScopeTypeFilter,
    onMarketShareScopeTypeFilterChangeCallback,
    getGameMarketShareForKey,
    getGameMarketShareChange,
    handleQuarterChange,
  ])

  const sortOrderFor = useCallback(
    (id: MarketShareSortColumnId) => (activeSort.columnId === id ? activeSort.sortOrder : undefined),
    [activeSort.columnId, activeSort.sortOrder]
  )

  // `filteredTopGames` is already scoped to the right Top 500 list (by recomputed Top 500 value)
  // based on the active sort column and whether a scope filter is active (see
  // `buildTopGamesTop500Filter`).
  const displayedTopGames = filteredTopGames

  const tableColumns: GRTableColumn<TopGame, typeof customProps, MarketShareSortColumnId>[] = useMemo(
    () => [
      {
        labelAccessor: ({ customTableProps }) => (
          <>
            {customTableProps && customTableProps.marketShareScopeTypeFilter && (
              <Box mx={2}>
                {filterGenreType === 'string' ? (
                  <Typography>{customTableProps.marketShareScopeTypeFilter.name}</Typography>
                ) : (
                  <FormControl size="small" variant="outlined" fullWidth>
                    <Select
                      disabled={!hasAccessToMarketShare}
                      value={customTableProps.marketShareScopeTypeFilter.id}
                      onChange={(event) => handleMarketShareScopeTypeFilterChange(event, customTableProps.marketShareScopeTypeFilters)}
                    >
                      {customTableProps.marketShareScopeTypeFilters.map((filter) => {
                        return (
                          <MenuItem key={filter.id} value={filter.id}>
                            {filter.name.length > 40 ? `${filter.name.substring(0, 40)}...` : filter.name}
                          </MenuItem>
                        )
                      })}
                    </Select>
                  </FormControl>
                )}
              </Box>
            )}
          </>
        ),
        columns: [
          {
            labelAccessor: ({ customTableProps }) => (
              <>
                {customTableProps && customTableProps.marketShareScopeTypeFilter && (
                  <Box mx={3} mt={1}>
                    <GRTooltip content={t(`market-share:${customTableProps.marketShareScopeType.id}_share_tooltip`)}>
                      <FormControlLabel
                        disabled={
                          customTableProps.marketShareScopeTypeFilter.id === MarketShareScopeTypeFilterAll ||
                          customTableProps.marketShareNumberType.id === 'value'
                        }
                        control={
                          <Switch
                            checked={
                              customTableProps.marketShareScopeTypeFilter.id === MarketShareScopeTypeFilterAll ||
                              customTableProps.marketShareNumberType.id === 'value'
                                ? false
                                : customTableProps.showScopeTypeShare
                            }
                            onChange={() => customTableProps.setShowScopeTypeShare(!customTableProps?.showScopeTypeShare)}
                            color="primary"
                            size="small"
                          />
                        }
                        label={t<string>(`market-share:show_${customTableProps.marketShareScopeType.id}_share`)}
                      />
                    </GRTooltip>
                  </Box>
                )}
              </>
            ),
            accessor: ({ row: topGame, rowIndex }) => (
              <Grid container alignItems="center" justifyContent="center" spacing={2}>
                <Grid item xs>
                  <TopGameCardContent topGame={topGame} rank={rowIndex + 1} variant="table" />
                </Grid>
                <Grid item>{topGame.reviewId && topGame.reviewPublished && <ReviewIndicator onClick={() => setAnalystOverviewDialogGame(topGame)} />}</Grid>
              </Grid>
            ),
            cellProps: { align: 'left' },
            headerCellProps: { align: 'left' },
          },
        ],
      },
      // Q1
      {
        labelAccessor: ({ customTableProps }) => (
          <>
            {customTableProps && (
              <QuarterSelector
                quarterType={quarterType}
                selectedQuarter={customTableProps.selectedQuarters[0]}
                quarters={calculatedQuarters}
                onQuarterChangeCallback={(e) => customTableProps.handleQuarterChange(e, 0)}
              />
            )}
          </>
        ),
        columns: [
          {
            id: MarketShareSortColumnId.Q1Revenue,
            labelAccessor: () => <GRTooltip content={t('market-share:revenue_tooltip')}>{t('common:revenue_shorthand')}</GRTooltip>,
            accessor: ({ row: topGame, customTableProps }) => {
              return (
                <strong>
                  {customTableProps && <>{customTableProps.getGameMarketShareForKey(topGame, 'revenue', customTableProps.selectedQuarters[0])}</>}
                </strong>
              )
            },
            sortable: true,
            sortAccessor: ({ row: topGame, customTableProps }) => {
              if (customTableProps) {
                const q = customTableProps.selectedQuarters[0].quarterIdentifier
                return customTableProps.marketShareNumberType.id === MarketShareNumberTypeId.Value
                  ? topGame.marketLevelQuarters[q].revenueValue
                  : topGame.marketLevelQuarters[q].revenueTop500Value
              }

              return 0
            },
            sortOrder: sortOrderFor(MarketShareSortColumnId.Q1Revenue),
            defaultSortOrder: SortOrder.DESC,
            cellProps: { align: 'center' },
            headerCellProps: { sx: { width: 80 }, align: 'center' },
          },
          {
            id: MarketShareSortColumnId.Q1RevenueChange,
            labelAccessor: () => <GRTooltip content={t('market-share:revenue_change_compared_tooltip')}>{t('common:change_text')}</GRTooltip>,
            accessor: ({ row: topGame, customTableProps }) => (
              <>
                {customTableProps && (
                  <TrendIndicator
                    value={customTableProps.getGameMarketShareChange(
                      topGame,
                      'revenue',
                      customTableProps.selectedQuarters[0].quarterIdentifier,
                      customTableProps.selectedQuarters[1].quarterIdentifier
                    )}
                    maximumFractionDigits={2}
                    type={TrendIndicatorType.Value}
                  />
                )}
              </>
            ),
            sortable: true,
            sortAccessor: ({ row: topGame, customTableProps }) =>
              quarterService.grTableSortAccessorForTopGameRevenueChange(
                customTableProps?.selectedQuarters[0].quarterIdentifier,
                customTableProps?.selectedQuarters[1].quarterIdentifier,
                topGame.marketLevelQuarters,
                customTableProps?.marketShareNumberType.id
              ),
            sortOrder: sortOrderFor(MarketShareSortColumnId.Q1RevenueChange),
            cellProps: { align: 'center' },
            headerCellProps: { sx: { width: 80 }, align: 'center' },
          },
          {
            id: MarketShareSortColumnId.Q1Downloads,
            labelAccessor: () => <GRTooltip content={t('market-share:downloads_tooltip')}>{t('common:downloads_shorthand')}</GRTooltip>,
            accessor: ({ row: topGame, customTableProps }) => (
              <strong>
                {customTableProps && <>{customTableProps.getGameMarketShareForKey(topGame, 'downloads', customTableProps.selectedQuarters[0])}</>}
              </strong>
            ),
            sortable: true,
            sortAccessor: ({ row: topGame, customTableProps }) => {
              if (customTableProps) {
                const q = customTableProps.selectedQuarters[0].quarterIdentifier
                return customTableProps.marketShareNumberType.id === MarketShareNumberTypeId.Value
                  ? topGame.marketLevelQuarters[q].downloadsValue
                  : topGame.marketLevelQuarters[q].downloadsTop500Value
              }

              return 0
            },
            sortOrder: sortOrderFor(MarketShareSortColumnId.Q1Downloads),
            cellProps: { align: 'center' },
            headerCellProps: { sx: { width: 80 }, align: 'center' },
          },
          {
            id: MarketShareSortColumnId.Q1DownloadsChange,
            labelAccessor: () => <GRTooltip content={t('market-share:downloads_change_compared_tooltip')}>{t('common:change_text')}</GRTooltip>,
            accessor: ({ row: topGame, customTableProps }) => (
              <>
                {customTableProps && (
                  <TrendIndicator
                    value={customTableProps.getGameMarketShareChange(
                      topGame,
                      'downloads',
                      customTableProps.selectedQuarters[0].quarterIdentifier,
                      customTableProps.selectedQuarters[1].quarterIdentifier
                    )}
                    maximumFractionDigits={2}
                    type={TrendIndicatorType.Value}
                  />
                )}
              </>
            ),
            sortable: true,
            sortAccessor: ({ row: topGame, customTableProps }) =>
              quarterService.grTableSortAccessorForTopGameDownloadsChange(
                customTableProps?.selectedQuarters[0].quarterIdentifier,
                customTableProps?.selectedQuarters[1].quarterIdentifier,
                topGame.marketLevelQuarters,
                customTableProps?.marketShareNumberType.id
              ),
            sortOrder: sortOrderFor(MarketShareSortColumnId.Q1DownloadsChange),
            cellProps: { align: 'center' },
            headerCellProps: { sx: { width: 80 }, align: 'center' },
          },
        ],
      },
      // Q2
      {
        labelAccessor: ({ customTableProps }) => (
          <>
            {customTableProps && (
              <QuarterSelector
                quarterType={quarterType}
                selectedQuarter={customTableProps.selectedQuarters[1]}
                quarters={calculatedQuarters}
                onQuarterChangeCallback={(e) => customTableProps.handleQuarterChange(e, 1)}
              />
            )}
          </>
        ),
        columns: [
          {
            id: MarketShareSortColumnId.Q2Revenue,
            labelAccessor: () => <GRTooltip content={t('market-share:revenue_tooltip')}>{t('common:revenue_shorthand')}</GRTooltip>,
            accessor: ({ row: topGame, customTableProps }) => (
              <strong>{customTableProps && <>{customTableProps.getGameMarketShareForKey(topGame, 'revenue', customTableProps.selectedQuarters[1])}</>}</strong>
            ),
            sortable: true,
            sortAccessor: ({ row: topGame, customTableProps }) => {
              if (customTableProps) {
                const q = customTableProps.selectedQuarters[1].quarterIdentifier
                return customTableProps.marketShareNumberType.id === MarketShareNumberTypeId.Value
                  ? topGame.marketLevelQuarters[q].revenueValue
                  : topGame.marketLevelQuarters[q].revenueTop500Value
              }

              return 0
            },
            sortOrder: sortOrderFor(MarketShareSortColumnId.Q2Revenue),
            cellProps: { align: 'center' },
            headerCellProps: { sx: { width: 80 }, align: 'center' },
          },
          {
            id: MarketShareSortColumnId.Q2RevenueChange,
            labelAccessor: () => <GRTooltip content={t('market-share:revenue_change_compared_tooltip')}>{t('common:change_text')}</GRTooltip>,
            accessor: ({ row: topGame, customTableProps }) => (
              <>
                {customTableProps && (
                  <TrendIndicator
                    value={customTableProps.getGameMarketShareChange(
                      topGame,
                      'revenue',
                      customTableProps.selectedQuarters[1].quarterIdentifier,
                      customTableProps.selectedQuarters[0].quarterIdentifier
                    )}
                    maximumFractionDigits={2}
                    type={TrendIndicatorType.Value}
                  />
                )}
              </>
            ),
            sortable: true,
            sortAccessor: ({ row: topGame, customTableProps }) =>
              quarterService.grTableSortAccessorForTopGameRevenueChange(
                customTableProps?.selectedQuarters[1].quarterIdentifier,
                customTableProps?.selectedQuarters[0].quarterIdentifier,
                topGame.marketLevelQuarters,
                customTableProps?.marketShareNumberType.id
              ),
            sortOrder: sortOrderFor(MarketShareSortColumnId.Q2RevenueChange),
            cellProps: { align: 'center' },
            headerCellProps: { sx: { width: 80 }, align: 'center' },
          },
          {
            id: MarketShareSortColumnId.Q2Downloads,
            labelAccessor: () => <GRTooltip content={t('market-share:downloads_tooltip')}>{t('common:downloads_shorthand')}</GRTooltip>,
            accessor: ({ row: topGame, customTableProps }) => (
              <strong>
                {customTableProps && <>{customTableProps.getGameMarketShareForKey(topGame, 'downloads', customTableProps.selectedQuarters[1])}</>}
              </strong>
            ),
            sortable: true,
            sortAccessor: ({ row: topGame, customTableProps }) => {
              if (customTableProps) {
                const q = customTableProps.selectedQuarters[1].quarterIdentifier
                return customTableProps.marketShareNumberType.id === MarketShareNumberTypeId.Value
                  ? topGame.marketLevelQuarters[q].downloadsValue
                  : topGame.marketLevelQuarters[q].downloadsTop500Value
              }

              return 0
            },
            sortOrder: sortOrderFor(MarketShareSortColumnId.Q2Downloads),
            cellProps: { align: 'center' },
            headerCellProps: { sx: { width: 80 }, align: 'center' },
          },
          {
            id: MarketShareSortColumnId.Q2DownloadsChange,
            labelAccessor: () => <GRTooltip content={t('market-share:downloads_change_compared_tooltip')}>{t('common:change_text')}</GRTooltip>,
            accessor: ({ row: topGame, customTableProps }) => (
              <>
                {customTableProps && (
                  <TrendIndicator
                    value={customTableProps.getGameMarketShareChange(
                      topGame,
                      'downloads',
                      customTableProps.selectedQuarters[1].quarterIdentifier,
                      customTableProps.selectedQuarters[0].quarterIdentifier
                    )}
                    maximumFractionDigits={2}
                    type={TrendIndicatorType.Value}
                  />
                )}
              </>
            ),
            sortable: true,
            sortAccessor: ({ row: topGame, customTableProps }) =>
              quarterService.grTableSortAccessorForTopGameDownloadsChange(
                customTableProps?.selectedQuarters[1].quarterIdentifier,
                customTableProps?.selectedQuarters[0].quarterIdentifier,
                topGame.marketLevelQuarters,
                customTableProps?.marketShareNumberType.id
              ),
            sortOrder: sortOrderFor(MarketShareSortColumnId.Q2DownloadsChange),
            cellProps: { align: 'center' },
            headerCellProps: { sx: { width: 80 }, align: 'center' },
          },
        ],
      },
    ],
    [filterGenreType, hasAccessToMarketShare, handleMarketShareScopeTypeFilterChange, t, quarterType, calculatedQuarters, sortOrderFor]
  )

  const [columns, setColumns] = useState<GRTableColumn<TopGame, typeof customProps, MarketShareSortColumnId>[]>(tableColumns)

  const findSortedColumn = useCallback(
    (
      cols: GRTableColumn<TopGame, typeof customProps, MarketShareSortColumnId>[]
    ): GRTableColumn<TopGame, typeof customProps, MarketShareSortColumnId> | undefined => {
      for (const col of cols) {
        if (col.sortable && col.sortOrder && col.id) {
          return col
        }
        if (col.columns?.length) {
          const found = findSortedColumn(col.columns)
          if (found) return found
        }
      }
      return undefined
    },
    []
  )

  const handleColumnsUpdate = useCallback(
    (updatedColumns: GRTableColumn<TopGame, typeof customProps, MarketShareSortColumnId>[]) => {
      setColumns(updatedColumns)
      if (onSortChange) {
        const sorted = findSortedColumn(updatedColumns)
        if (sorted && sorted.id && sorted.sortOrder && (sorted.id !== activeSort.columnId || sorted.sortOrder !== activeSort.sortOrder)) {
          onSortChange({ columnId: sorted.id, sortOrder: sorted.sortOrder })
        }
      }
    },
    [onSortChange, findSortedColumn, activeSort.columnId, activeSort.sortOrder]
  )

  useEffect(() => {
    setColumns(tableColumns)
  }, [tableColumns])

  return (
    <div className="MarketShareTopGames">
      <TableContainer>
        <GRTable
          rows={displayedTopGames}
          maxRows={maxRows}
          columns={columns}
          rowIdKey="id"
          striped
          hoverable
          onColumnsUpdated={handleColumnsUpdate}
          customProps={customProps}
          noRowsLabel={t('common:no_games_available')}
        />
      </TableContainer>

      {analystOverviewDialogGame && analystOverviewDialogGame.reviewId && (
        <AnalystOverviewDialog
          reviewId={analystOverviewDialogGame?.reviewId}
          gameName={analystOverviewDialogGame?.name}
          open={!!analystOverviewDialogGame}
          onClose={() => setAnalystOverviewDialogGame(null)}
        />
      )}
    </div>
  )
}

export default MarketShareTopGames
